import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, IconButton, MenuItem, Tooltip } from '@mui/material';
import type { CrewField } from '../RaptorJobCreateModal';
import Autocomplete from '@/common/presentation/components/inputs/Autocomplete';
import Select from '@/common/presentation/components/inputs/Select';
import tid from '@/common/application/utils/tid';
import type { CrewMemberListItem } from '@/raptorjob/presentation/utils/crewRoleAssignment';
import { CertificationWarningIcon } from '@/raptorjob/presentation/components/RaptorJobDetails/components/JobTicketOverview/JobTicketOverviewERP/components/CertificationWarningIcon';
import { STATUS_COLORS } from '@/common/presentation/constants/colors';
import { useCrewRowEmployeeAutocompleteState, type CrewEmployeeOption } from './useCrewRowEmployeeAutocompleteState';
import { CrewEmployeeAutocompleteListItem } from './CrewEmployeeAutocompleteListItem';

interface EmployeeResource {
  id: number;
}

interface JobRoleOption {
  id: number;
  name?: string | null;
}

interface CrewFieldRowProps {
  crew: CrewField;
  supportedJobRoles: JobRoleOption[] | undefined;
  loadingJobRoles: boolean;
  loadingEmployees: boolean;
  crewMembers: CrewMemberListItem[] | null | undefined;
  buildEmployeeOptionsForRole: (roleId: number | null) => CrewEmployeeOption[];
  onRoleChange: (rowId: string, roleId: number, roleName: string) => void;
  onEmployeeSelect: (rowId: string, employeeId: number | null, employeeName: string, status?: string[]) => void;
  onDeleteRow: (rowId: string) => void;
  setEmployeeSearchTerm: (term: string) => void;
  isEmployeeCompliant?: (employee: EmployeeResource, roleId: number) => boolean;
  getEmployeeMissingCerts?: (employee: EmployeeResource, roleId: number) => string[];
}

export function CrewFieldRow({
  crew,
  supportedJobRoles,
  loadingJobRoles,
  loadingEmployees,
  crewMembers,
  buildEmployeeOptionsForRole,
  onRoleChange,
  onEmployeeSelect,
  onDeleteRow,
  setEmployeeSearchTerm,
  isEmployeeCompliant,
  getEmployeeMissingCerts
}: CrewFieldRowProps) {
  const employeeOptions = buildEmployeeOptionsForRole(crew.roleId);
  const { optionsWithCurrentEmployee, selectedValue } = useCrewRowEmployeeAutocompleteState(
    crew,
    employeeOptions,
    crewMembers,
    loadingEmployees
  );

  const selectedEmployeeResource = crew.employeeId != null ? { id: crew.employeeId } : null;
  const isSelectedCompliant =
    selectedEmployeeResource && crew.roleId && isEmployeeCompliant ? isEmployeeCompliant(selectedEmployeeResource, crew.roleId) : true;
  const selectedMissingCerts =
    selectedEmployeeResource && crew.roleId && getEmployeeMissingCerts && !isSelectedCompliant
      ? getEmployeeMissingCerts(selectedEmployeeResource, crew.roleId)
      : [];

  const complianceAdornment =
    isEmployeeCompliant !== undefined && crew.employeeId != null && crew.roleId != null ? (
      isSelectedCompliant ? (
        <Tooltip title="Meets all certification requirements" placement="top">
          <CheckCircleIcon sx={{ fontSize: 16, color: STATUS_COLORS.SUCCESS_MAIN }} />
        </Tooltip>
      ) : (
        <CertificationWarningIcon tooltipText={`Missing certifications: ${selectedMissingCerts.join(', ')}`} />
      )
    ) : undefined;

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
      <Select
        fullWidth
        required
        value={crew.roleId ?? ''}
        label="Role"
        disabled={loadingJobRoles}
        onChange={(e) => {
          const selectedRole = supportedJobRoles?.find((role) => role.id === Number(e.target.value));
          if (selectedRole) onRoleChange(crew.rowId, selectedRole.id, selectedRole.name || '');
        }}
        {...tid('jobs:create:crew-role-select')}
      >
        {(supportedJobRoles || []).map((role) => (
          <MenuItem key={role.id} value={role.id}>
            {role.name}
          </MenuItem>
        ))}
      </Select>
      <Autocomplete
        fullWidth
        required
        options={optionsWithCurrentEmployee}
        loading={crew.roleId != null && loadingEmployees}
        optionsloading={crew.roleId != null && loadingEmployees}
        disabled={crew.roleId == null}
        value={selectedValue}
        startAdornment={complianceAdornment}
        onChange={(_, newValue) => {
          const picked = Array.isArray(newValue) ? newValue[0] : newValue;
          onEmployeeSelect(crew.rowId, picked?.id || null, picked?.name || '', picked?.status);
        }}
        onInputChange={(_, inputValue, reason) => {
          if (crew.roleId == null) return;
          if (reason === 'input') setEmployeeSearchTerm(inputValue);
          else if (reason === 'clear') setEmployeeSearchTerm('');
        }}
        clearOnBlur={false}
        getOptionLabel={(option) => option.name}
        getOptionKey={(option) => option.id}
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        placeholder={crew.roleId != null ? 'Employee' : 'Select a Role first'}
        label="Employee"
        sx={{ width: '100%' }}
        renderOption={(props, option) => (
          <CrewEmployeeAutocompleteListItem
            listProps={props}
            option={option}
            roleId={crew.roleId}
            isEmployeeCompliant={isEmployeeCompliant}
            getEmployeeMissingCerts={getEmployeeMissingCerts}
          />
        )}
        {...tid('jobs:create:crew-autocomplete')}
      />

      <IconButton onClick={() => onDeleteRow(crew.rowId)} sx={{ color: 'error.main' }}>
        <DeleteOutlinedIcon />
      </IconButton>
    </Box>
  );
}
